import { useIsMobile } from '../hooks/useIsMobile';
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, CircularProgress,
  Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
  Table, TableHead, TableBody, TableRow, TableCell, Chip
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Sidebar from '../components/Sidebar';
import { HoursBarChart, HoursLineChart, MultiLineChart } from '../components/SummaryCharts';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SIDEBAR_WIDTH = 230;

function StatCard({ label, value, sub, icon, color = '#1a4d2e' }) {
  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 500, color, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {sub && <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>{sub}</Typography>}
          </Box>
          <Box sx={{ color: '#d0e8d0', mt: 0.5 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Card>
  );
}

export default function SummaryPage() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState(0);
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [months]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [myRes] = await Promise.all([
        api.get(`/attendance/summary/?months=${months}`),
      ]);
      setData(myRes.data);

      if (isAdmin) {
        const adminRes = await api.get(`/attendance/summary/admin/?months=${months}`);
        setAdminData(adminRes.data);
      }
    } catch (err) {
      console.error('Summary fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{ ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`, p: isMobile ? 2 : 3, pt: isMobile ? 7 : 3, ...(isMobile && { width: '100%' }) }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Attendance Summary</Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            >
              <MenuItem value={1}>Last 1 month</MenuItem>
              <MenuItem value={3}>Last 3 months</MenuItem>
              <MenuItem value={6}>Last 6 months</MenuItem>
              <MenuItem value={12}>Last 12 months</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: '#1a4d2e' }} />
          </Box>
        ) : (
          <>
            {/* Admin tabs */}
            {isAdmin && (
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: '0.5px solid #e8ede8' }}
                TabIndicatorProps={{ style: { background: '#1a4d2e' } }}
              >
                <Tab label="My Summary" sx={{ fontSize: 13, textTransform: 'none', '&.Mui-selected': { color: '#1a4d2e' } }} />
                <Tab label="All Employees" sx={{ fontSize: 13, textTransform: 'none', '&.Mui-selected': { color: '#1a4d2e' } }} />
              </Tabs>
            )}

            {/* My Summary tab */}
            {(tab === 0 || !isAdmin) && data && (
              <Box>
                {/* Stat cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                  <StatCard
                    label="Days present"
                    value={data.cards.total_days_present}
                    sub="Unique days clocked in"
                    icon={<CalendarMonthIcon />}
                  />
                  <StatCard
                    label="Total hours"
                    value={`${data.cards.total_hours}h`}
                    sub="All completed sessions"
                    icon={<AccessTimeIcon />}
                  />
                  <StatCard
                    label="Avg hours/day"
                    value={`${data.cards.avg_hours_per_day}h`}
                    sub="Per working day"
                    icon={<TrendingUpIcon />}
                  />
                  <StatCard
                    label="Total sessions"
                    value={data.cards.total_records}
                    sub="Clock-in records"
                    icon={<AccessTimeIcon />}
                  />
                </Box>

                {/* Charts */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: { xs: '1fr', md: '1fr 1fr' } }, gap: 3, mb: 3 }}>
                  <ChartCard title="Daily hours worked">
                    <HoursBarChart
                      data={data.daily.slice(-14)}
                      dataKey="hours"
                      xKey="date"
                      label="Hours"
                    />
                  </ChartCard>
                  <ChartCard title="Weekly hours trend">
                    <HoursBarChart
                      data={data.weekly}
                      dataKey="hours"
                      xKey="week"
                      label="Hours"
                    />
                  </ChartCard>
                </Box>

                <ChartCard title="Monthly hours trend">
                  <HoursLineChart
                    data={data.monthly}
                    dataKey="hours"
                    xKey="month_short"
                    label="Hours worked"
                  />
                </ChartCard>

                {/* Monthly breakdown table */}
                <Card sx={{ mt: 3 }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Monthly breakdown</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell>Days present</TableCell>
                        <TableCell>Total hours</TableCell>
                        <TableCell>Sessions</TableCell>
                        <TableCell>Avg hrs/session</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.monthly.map((m) => (
                        <TableRow key={m.month} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{m.month}</TableCell>
                          <TableCell>{m.days}</TableCell>
                          <TableCell>{m.hours}h</TableCell>
                          <TableCell>{m.sessions}</TableCell>
                          <TableCell>
                            {m.sessions > 0 ? `${Math.round((m.hours / m.sessions) * 10) / 10}h` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.monthly.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#aaa', fontSize: 13 }}>
                            No data for selected period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </Box>
            )}

            {/* Admin — All Employees tab */}
            {tab === 1 && isAdmin && adminData && (
              <Box>
                {/* Admin stat cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                  <StatCard
                    label="Total hours logged"
                    value={`${adminData.cards.total_hours_logged}h`}
                    sub="All employees combined"
                    icon={<AccessTimeIcon />}
                  />
                  <StatCard
                    label="Active employees"
                    value={adminData.cards.total_employees_active}
                    sub="With attendance records"
                    icon={<PeopleIcon />}
                  />
                  <StatCard
                    label="Total sessions"
                    value={adminData.cards.total_sessions}
                    sub="All clock-in records"
                    icon={<AccessTimeIcon />}
                  />
                  <StatCard
                    label="Avg hrs/employee"
                    value={`${adminData.cards.avg_hours_per_employee}h`}
                    sub="Per active employee"
                    icon={<TrendingUpIcon />}
                  />
                </Box>

                {/* Charts */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                  <ChartCard title="Monthly hours trend">
                    <HoursLineChart
                      data={adminData.monthly_trend}
                      dataKey="total_hours"
                      xKey="month"
                      label="Total hours"
                    />
                  </ChartCard>
                  <ChartCard title="Hours by department">
                    <HoursBarChart
                      data={adminData.per_department}
                      dataKey="total_hours"
                      xKey="department"
                      label="Hours"
                    />
                  </ChartCard>
                </Box>

                {/* Per employee table */}
                <Card sx={{ mb: 3 }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Employee breakdown</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Days present</TableCell>
                        <TableCell>Total hours</TableCell>
                        <TableCell>Avg hrs/day</TableCell>
                        <TableCell>Sessions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminData.per_employee.map((e) => (
                        <TableRow key={e.id} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{e.name}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#888' }}>@{e.username}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={e.department}
                              size="small"
                              sx={{ background: '#eaf3de', color: '#27500a', fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell>{e.total_days}</TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#1a4d2e' }}>{e.total_hours}h</TableCell>
                          <TableCell>{e.avg_hours_per_day}h</TableCell>
                          <TableCell>{e.total_sessions}</TableCell>
                        </TableRow>
                      ))}
                      {adminData.per_employee.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#aaa', fontSize: 13 }}>
                            No data for selected period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>

                {/* Per department table */}
                <Card>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Department breakdown</Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell>Employees</TableCell>
                        <TableCell>Total hours</TableCell>
                        <TableCell>Total days</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminData.per_department.map((d) => (
                        <TableRow key={d.department} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{d.department}</TableCell>
                          <TableCell>{d.employee_count}</TableCell>
                          <TableCell sx={{ color: '#1a4d2e', fontWeight: 500 }}>{d.total_hours}h</TableCell>
                          <TableCell>{d.total_days}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}